import { useContext, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type { User } from '@/@types/global';
import { endpoints } from '@/react-query/endpoints';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { useUpdateTrialStatus } from '../hooks';
import type { CancelCheckboxReason, TrialStatusData } from '../types';
import { TrialStatus } from '../types';

import { CancelConfirmation } from './CancelConfirmation';
import { SubscriptionDetails } from './SubscriptionDetails';

type ManageSubscriptionView = 'PLAN_DETAILS' | 'CANCEL_CONFIRMATION';

interface Props {
  isLoading?: boolean;
  onClose: () => void;
  trialStatusData?: TrialStatusData;
}

export const ManageSubscription = ({
  isLoading,
  onClose,
  trialStatusData,
}: Props) => {
  const [view, setView] = useState<ManageSubscriptionView>('PLAN_DETAILS');

  const {
    mutateAsync: updateTrialStatus,
    isLoading: isUpdating,
    isError: isUpdateError,
  } = useUpdateTrialStatus();
  const queryClient = useQueryClient();
  const { trackEvent } = useTrackEvent();
  const externalPatientContext = useContext(ExternalPatientContext);
  const user = useSelector(
    (state: { login?: { user?: User } }) => state.login?.user,
  );
  const primaryPracticeId = user?.primaryPracticeId;
  const primaryOrganizationId = user?.primaryOrganizationId;

  const handleCancelSubscription = async (
    reasons: CancelCheckboxReason[],
    otherReason: string,
  ) => {
    try {
      trackEvent(mixpanelEvents.externalCancelClick, {
        ...externalPatientContext,
        reasons,
        otherReason,
      });
      await updateTrialStatus({
        status: TrialStatus.CANCEL_REQUEST,
        metadata: { practiceCancellationReason: { reasons, otherReason } },
      });
      queryClient.invalidateQueries({
        queryKey: [
          endpoints.reverseTrial,
          primaryOrganizationId,
          primaryPracticeId,
          endpoints.status,
        ],
      });
      setView('PLAN_DETAILS');
    } catch (e) {
      console.error(e);
    }
  };

  switch (view) {
    case 'PLAN_DETAILS':
      return (
        <SubscriptionDetails
          isLoading={isLoading}
          onClickCancelSubscription={() => {
            trackEvent(mixpanelEvents.externalCancelInitiate, {
              ...externalPatientContext,
            });
            setView('CANCEL_CONFIRMATION');
          }}
          onClose={onClose}
          paidStartedOn={trialStatusData?.paidStartedOn}
          trialEndsOn={trialStatusData?.trialEndsOn}
          trialStartedOn={trialStatusData?.trialStartedOn}
          trialStatus={trialStatusData?.status}
        />
      );
    case 'CANCEL_CONFIRMATION':
      return (
        <CancelConfirmation
          isError={isUpdateError}
          isLoading={isUpdating}
          onCancel={handleCancelSubscription}
          onClose={() => {
            trackEvent(mixpanelEvents.externalCancelGoBack, {
              ...externalPatientContext,
            });
            setView('PLAN_DETAILS');
          }}
          trialStatus={trialStatusData?.status}
        />
      );
    default:
      return null;
  }
};
